import React, {
	useRef,
	createContext,
	useContext,
	type PropsWithChildren,
	useCallback,
	useMemo,
} from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay/hooks';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import type { Props as AsyncColumnPickerProps } from '@atlassian/jira-issue-table-column-picker/src/ui/main.tsx';
import type { optimisticUpdatesSelectedOptionsQuery$key as IFCOptimisticUpdatesKey } from '@atlassian/jira-relay/src/__generated__/optimisticUpdatesSelectedOptionsQuery.graphql';
import {
	COLUMN_ID_MERGED_COLUMN,
	KNOWN_COLUMN_TYPES,
} from '@atlassian/jira-issue-table-common/src/constants.tsx';
import type { context_nativeIssueTable_IFCConfigContextProvider_queryRef$key as IFCConfigContextProviderKey } from '@atlassian/jira-relay/src/__generated__/context_nativeIssueTable_IFCConfigContextProvider_queryRef.graphql';
import type { context_nativeIssueTable_IFCConfigContextProvider_columnsQueryRef$key as FieldConfigKey } from '@atlassian/jira-relay/src/__generated__/context_nativeIssueTable_IFCConfigContextProvider_columnsQueryRef.graphql';
import { deleteOptimisticFieldConfig } from '@atlassian/jira-issue-table-column-picker/src/ui/utils.tsx';
import {
	useConnectionsList,
	type ConnectionDetails,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/connections-list/index.tsx';
import { ADD_COLUMNS } from '@atlassian/jira-issue-table-column-picker/src/common/constants.tsx';
import type { IFCConfigType } from '../types.tsx';
import type { Column } from '../../../types.tsx';
import { COLUMN_ID_INTERNAL_COLUMN } from '../../../constants.tsx';
import {
	TEMP_CREATE_FIELDSET_ID,
	updateOptimisticFieldName,
	optimisticUpdateColumnConfigFieldSetIdById,
} from '../column-picker-footer/utils.tsx';
import {
	getSelectedOptions,
	optimisticUpdatesSelectedOptions,
	optimisticUpdateIssuesFieldSetId,
} from '../optimistic-updates/index.tsx';

export type Field = {
	id: string;
	field: { name: string; type: string; options?: { id: string; value: string }[] | undefined };
};

type IFCConfigContextType = {
	ifcConfig: IFCConfigType;
	addFieldToColumns?: (
		fieldSetId: string,
		index?: number,
		onErrorCallback?: () => void,
		update?: boolean,
	) => void;
	updateSelectedOptions?: (
		fieldId: string,
		type: string,
		options: { id: string; value: string }[],
	) => void;
	optimisticUpdateField?: (
		fieldId: string,
		field: {
			name: string;
			type: string;
			options?: { id: string; value: string }[] | undefined;
		},
	) => Field | undefined;
	replaceFieldSetInColumns?: (
		fieldSetId: string,
		newfieldSetId: string,
		fieldId: string,
		onSuccessCallback?: () => void,
		onErrorCallback?: () => void,
	) => void;
	optimisticDeleteField?: (newfieldSetId: string) => void;
};

export const IFCConfigContext = createContext<IFCConfigContextType>({ ifcConfig: undefined });

export const IFCConfigContextProvider = ({
	children,
	ifcConfig,
	columns,
	onColumnsChange,
	queryRef,
	columnsQueryRef,
}: PropsWithChildren<{
	ifcConfig: IFCConfigType;
	columns: Column[];
	onColumnsChange: AsyncColumnPickerProps['onChange'];
	queryRef: IFCConfigContextProviderKey | null;
	columnsQueryRef: FieldConfigKey | null;
}>) => {
	const columnRef = useRef(columns);

	const currentIssuesRef = useRef<IFCOptimisticUpdatesKey | null>(null);

	const processedIssuesRef = useRef<{
		issuesRef: IFCOptimisticUpdatesKey | null;
		fieldId: string | null;
	}>({
		issuesRef: null,
		fieldId: null,
	});

	const selectedOptions = useRef<{ [key: string]: string } | undefined>(undefined);

	if (columnRef.current !== columns) {
		columnRef.current = columns;
	}

	const environment = useRelayEnvironment();

	const { connections } = useConnectionsList();
	const currentConnections = useRef<{ [connectionId: string]: ConnectionDetails }>();
	const currentGroupsIssuesRef = useRef<(IFCOptimisticUpdatesKey | undefined)[]>([]);

	if (currentConnections.current !== connections) {
		currentGroupsIssuesRef.current = Object.values(connections)
			.filter(
				(connection) =>
					connection.type === 'GROUP_CHILDREN' || connection.type === 'PARENT_CHILDREN',
			)
			.map((connection) =>
				connection.type === 'GROUP_CHILDREN' || connection.type === 'PARENT_CHILDREN'
					? connection.fieldSetsData
					: undefined,
			);
		currentConnections.current = connections;
	}

	const issuesQueryRef = useFragment<IFCConfigContextProviderKey>(
		graphql`
			fragment context_nativeIssueTable_IFCConfigContextProvider_queryRef on JiraIssueConnection {
				...optimisticUpdatesSelectedOptionsQuery
			}
		`,
		queryRef,
	);

	const fieldConfig = useFragment<FieldConfigKey>(
		graphql`
			fragment context_nativeIssueTable_IFCConfigContextProvider_columnsQueryRef on JiraIssueSearchFieldSetConnection {
				__id
			}
		`,
		columnsQueryRef,
	);

	if (currentIssuesRef.current !== issuesQueryRef) {
		currentIssuesRef.current = issuesQueryRef ?? null;
	}

	const optimisticDeleteField = useCallback(
		(fieldSetId: string) => {
			deleteOptimisticFieldConfig({
				environment,
				fieldConfigId: fieldConfig?.__id,
				fields: [
					{
						fieldSetId,
						name: '',
					},
				],
			});
		},
		[environment, fieldConfig?.__id],
	);

	const getColumnIds = useCallback(() => {
		const columnIds = columnRef.current
			?.map(({ id }) => id)
			?.filter((id) => !id.startsWith(COLUMN_ID_INTERNAL_COLUMN) && id !== TEMP_CREATE_FIELDSET_ID);

		const mergedColumnsIds = fg('jira_list_merged_column_gate')
			? (
					columnRef.current.find((col) => col?.id === COLUMN_ID_MERGED_COLUMN)?.mergedColumns ?? []
				).map((col) => col.id)
			: [];

		return [...mergedColumnsIds, ...columnIds];
	}, []);

	const replaceFieldSetInColumns = useCallback(
		(fieldSetId: string, newfieldSetId: string, fieldId: string) => {
			const columnIds = getColumnIds();
			let skipRefetch = true;

			// find out if a column of the same fieldsetid exists
			// if yes, the column will get merged when we refetch so we also don't skip refetch
			const duplicateFieldSetIndex = columnIds.findIndex((id) => id === newfieldSetId);
			if (duplicateFieldSetIndex > -1) {
				optimisticDeleteField(newfieldSetId);
				skipRefetch = false;
				columnIds.splice(duplicateFieldSetIndex, 1);
			}

			optimisticUpdateColumnConfigFieldSetIdById({
				environment,
				fieldConfigId: fieldConfig?.__id,
				field: {
					fieldId,
					fieldSetId: newfieldSetId,
				},
			});

			optimisticUpdateIssuesFieldSetId(
				environment,
				currentIssuesRef.current,
				fieldId,
				newfieldSetId,
			);

			// for groups
			currentGroupsIssuesRef.current.forEach((groupIssuesRef) => {
				if (groupIssuesRef != null) {
					optimisticUpdateIssuesFieldSetId(environment, groupIssuesRef, fieldId, newfieldSetId);
				}
			});

			const insertIndex = columnIds.findIndex((id) => id === fieldSetId);

			if (insertIndex > -1) {
				columnIds[insertIndex] = newfieldSetId;
				onColumnsChange?.({
					columnIds,
					retries: 3,
					onErrorCallback: () => {
						optimisticDeleteField(newfieldSetId);
					},
					isHorizontalAutoScrollNeeded: false,
					skipRefetch,
					analyticsAttributes: {
						fromIFCFieldUpdated: true,
						changeType: ADD_COLUMNS,
					},
				});
			}
		},
		[environment, fieldConfig?.__id, getColumnIds, onColumnsChange, optimisticDeleteField],
	);

	const addFieldToColumns = useCallback(
		(fieldSetId: string, index?: number, onErrorCallback?: () => void) => {
			const columnIds = getColumnIds();

			const insertIndex = index == null ? columnIds.length : index;
			columnIds.splice(insertIndex, 0, fieldSetId);
			onColumnsChange?.({
				columnIds,
				retries: 3,
				onErrorCallback,
				isHorizontalAutoScrollNeeded: index === undefined,
				analyticsAttributes: {
					fromIFCFieldCreated: true,
					changeType: ADD_COLUMNS,
				},
			});
		},
		[getColumnIds, onColumnsChange],
	);

	const updateSelectedOptions = useCallback(
		(fieldId: string, type: string, options: { id: string; value: string }[]) => {
			if (
				type !== KNOWN_COLUMN_TYPES.CHECKBOX_SELECT &&
				type !== KNOWN_COLUMN_TYPES.SINGLE_SELECT
			) {
				return;
			}

			if (
				processedIssuesRef.current.issuesRef !== currentIssuesRef.current ||
				processedIssuesRef.current.fieldId !== fieldId
			) {
				selectedOptions.current = getSelectedOptions(currentIssuesRef.current, fieldId, type);
				processedIssuesRef.current.issuesRef = currentIssuesRef.current;
				processedIssuesRef.current.fieldId = fieldId;
			}

			// for groups
			currentGroupsIssuesRef.current.forEach((groupIssuesRef) => {
				if (groupIssuesRef != null) {
					selectedOptions.current = {
						...selectedOptions.current,
						...getSelectedOptions(groupIssuesRef, fieldId, type),
					};
				}
			});

			if (selectedOptions.current) {
				return optimisticUpdatesSelectedOptions(
					environment,
					type,
					selectedOptions.current,
					options,
				);
			}
		},
		[environment],
	);

	const optimisticUpdateField = useCallback(
		(
			fieldId: string,
			field: {
				name: string;
				type: string;
				options?: { id: string; value: string }[] | undefined;
			},
		) => {
			if (fieldConfig?.__id) {
				const prevName = updateOptimisticFieldName({
					environment,
					fieldConfigId: fieldConfig?.__id,
					field: {
						fieldId,
						name: field.name,
					},
				});

				let prevOptions;
				if (field.options) {
					prevOptions = updateSelectedOptions?.(fieldId, field.type, field.options);
				}

				if (prevName) {
					return {
						id: fieldId,
						field: {
							name: prevName,
							type: field.type,
							options: prevOptions,
						},
					};
				}
			}
		},
		[environment, fieldConfig?.__id, updateSelectedOptions],
	);

	const value = useMemo(
		() => ({
			ifcConfig,
			addFieldToColumns,
			updateSelectedOptions,
			replaceFieldSetInColumns,
			optimisticUpdateField,
			optimisticDeleteField,
		}),
		[
			ifcConfig,
			addFieldToColumns,
			updateSelectedOptions,
			replaceFieldSetInColumns,
			optimisticUpdateField,
			optimisticDeleteField,
		],
	);

	return <IFCConfigContext.Provider value={value}>{children}</IFCConfigContext.Provider>;
};

export const useIFCConfigContext = () => {
	return useContext(IFCConfigContext);
};

export const IFCConfigContextProviderComponent = componentWithCondition(
	() => fg('jira_inline_field_config_gate'),
	IFCConfigContextProvider,
	({ children }: PropsWithChildren<{ ifcConfig: IFCConfigType }>) => children,
);
